/**
 * A drop zone for receipts and bank statements: one file, or several at once
 * where `multiple` is set.
 *
 * EXTRACTED FROM ReceiptDropzone on 2026-09-13, when the Reconcile screen
 * needed to attach bank statements. The drag handlers and the file input are
 * where the bugs were -- both handlers must `preventDefault` or the browser
 * navigates to the file, and the input must clear itself or re-picking the same
 * file after a failure fires nothing -- and a second copy would be a second
 * place to forget either. What differs is only WHERE the files go, so that is
 * the one thing passed in.
 *
 * SEVERAL FILES (2026-09-14). Receipts arrive in handfuls -- an invoice and its
 * card slip, a trip's worth of hotel and taxi -- and a zone that silently kept
 * the first of three dropped files lost the other two with no word said. So a
 * drop or a pick hands over EVERY file, in order, as one batch; `upload` owns
 * what a batch means. A bank statement is one file per reconciliation, so that
 * zone leaves `multiple` off and still receives a batch of one.
 *
 * The upload function owns the whole sequence (presign, POST to storage, record)
 * and throws on failure; this reports the failure and never swallows it.
 */

import { useState } from 'react';
import { ApiError } from '../api.js';

export interface FileDropzoneProps {
  /**
   * Presign, upload and record every file of one drop or pick, in the order
   * given -- exactly one file unless `multiple` is set. Throws to report a
   * failure.
   */
  upload: (files: File[]) => Promise<void>;
  onError: (message: string) => void;
  /** The file input's accessible name. */
  inputLabel: string;
  idleText: string;
  note?: string;
  compact?: boolean;
  /** Shown when the failure is not an ApiError. */
  failureText?: string;
  /** Accept several files from one drop or pick. Off means the first only. */
  multiple?: boolean;
  /** The file types offered by the picker. Receipts' and statements' by default. */
  accept?: string;
  /** What the zone says while `upload` runs. */
  busyText?: string;
}

export function FileDropzone({
  upload,
  onError,
  inputLabel,
  idleText,
  note,
  compact,
  failureText = 'Could not attach that file.',
  multiple = false,
  accept = 'application/pdf,image/jpeg,image/png,image/webp,image/heic',
  busyText = 'Uploading…',
}: FileDropzoneProps) {
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);

  async function attach(files: File[]) {
    setUploading(true);
    try {
      await upload(files);
    } catch (caught) {
      onError(caught instanceof ApiError ? caught.message : failureText);
    } finally {
      setUploading(false);
    }
  }

  return (
    <label
      className={`dropzone${compact ? ' is-compact' : ''}${dragging ? ' is-dragging' : ''}${
        uploading ? ' is-busy' : ''
      }`}
      onDragOver={(event) => {
        // Both handlers must preventDefault or the browser navigates to the
        // file instead, which loses the page and anything unsaved on it.
        event.preventDefault();
        if (!uploading) setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        const all = Array.from(event.dataTransfer.files ?? []);
        const dropped = multiple ? all : all.slice(0, 1);
        if (dropped.length > 0 && !uploading) void attach(dropped);
      }}
    >
      <input
        type="file"
        aria-label={inputLabel}
        accept={accept}
        multiple={multiple}
        disabled={uploading}
        onChange={(event) => {
          // Copied out BEFORE the input is cleared: the FileList is live, and
          // clearing the value empties it.
          const all = Array.from(event.target.files ?? []);
          const picked = multiple ? all : all.slice(0, 1);
          // Cleared so the same file can be chosen twice -- otherwise
          // re-picking after a failed upload fires no change event.
          event.target.value = '';
          if (picked.length > 0) void attach(picked);
        }}
      />
      <span className="dropzone-main">{uploading ? busyText : idleText}</span>
      {!compact && note && <span className="dropzone-note muted">{note}</span>}
    </label>
  );
}
